import { ManagerLink } from "@/components/ManagerLink";
import { useState, useRef, useEffect, useCallback, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ClubCrest from "@/components/ClubCrest";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { format } from "date-fns";
import { ArrowRightLeft, Home, Plane, TrendingUp } from "lucide-react";
import { useClubSlugMap } from "@/hooks/useClubDirectory";
import { getCurrentSeasonDates, type SeasonType } from "@/lib/seasonUtils";
import { Share2 } from "lucide-react";
import { IMAGE_SHARING_ENABLED } from "@/lib/featureFlags";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
const StandingsShareDialog = lazy(() => import("./StandingsShareDialog"));
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollX } from "@/components/ScrollX";

interface LeagueConfig {
  code: string;
  name: string;
  flag: string;
  tier: "top" | "second" | "other";
  seasonType: SeasonType;
}

interface LeagueQualificationConfig {
  ucl_spots: number;
  uel_spots: number;
  uecl_spots: number;
  relegation_zone: number;
  team_count: number;
  promotion_spots: number;
  playoff_spots: number;
}

// FLAG_MAP removed - using CountryFlag component instead

type ViewMode = "all" | "home" | "away" | "ppg";

interface TableRow {
  club_id: string;
  club_name: string;
  club_brand_primary?: string | null;
  club_brand_secondary?: string | null;
  manager_id: string | null;
  manager_name: string | null;
  manager_start_date: string | null;
  is_mid_season_appointment: boolean;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goals_for: number;
  goals_against: number;
  goal_difference: number;
  points: number;
  home_played: number;
  home_won: number;
  home_drawn: number;
  home_lost: number;
  home_gf: number;
  home_ga: number;
  home_gd: number;
  home_points: number;
  away_played: number;
  away_won: number;
  away_drawn: number;
  away_lost: number;
  away_gf: number;
  away_ga: number;
  away_gd: number;
  away_points: number;
  mgr_played: number;
  mgr_won: number;
  mgr_drawn: number;
  mgr_lost: number;
  mgr_gf: number;
  mgr_ga: number;
  mgr_gd: number;
  mgr_points: number;
  mgr_ppg: number;
}

interface CurrentLeagueTablesProps {
  onSelectManager?: (managerId: string) => void;
  activeLeague?: string;
}

// Get season boundaries based on league's season type
const getSeasonDatesForLeague = (seasonType: SeasonType = 'split-year') => {
  const { seasonLabel, startDate, endDate, isOffSeason } = getCurrentSeasonDates(seasonType);
  return { seasonLabel, startDate, endDate, isOffSeason };
};

interface LeagueTableViewProps {
  leagueCode: string;
  seasonType: SeasonType;
  onSelectManager?: (managerId: string) => void;
  qualificationConfig?: LeagueQualificationConfig | null;
  onTableLoaded?: (table: TableRow[]) => void;
}

// Zone a position falls into, driven by league configuration
type TableZone = "ucl" | "uel" | "uecl" | "promotion" | "playoff" | "relegation" | null;

const getPositionZone = (
  position: number,
  tableLength: number,
  config?: LeagueQualificationConfig | null,
): TableZone => {
  if (!config) {
    if (position <= 4) return "ucl";
    if (position >= tableLength - 2) return "relegation";
    return null;
  }

  const { ucl_spots, uel_spots, uecl_spots, relegation_zone, team_count, promotion_spots, playoff_spots } = config;
  const actualTeamCount = team_count || tableLength;

  const isSecondTier = promotion_spots > 0 && ucl_spots === 0 && uel_spots === 0 && uecl_spots === 0;

  if (isSecondTier) {
    if (position <= promotion_spots) return "promotion";
    if (playoff_spots > 0 && position <= promotion_spots + playoff_spots) return "playoff";
    if (relegation_zone > 0 && position > actualTeamCount - relegation_zone) return "relegation";
    return null;
  }

  if (ucl_spots > 0 && position <= ucl_spots) return "ucl";
  if (uel_spots > 0 && position <= ucl_spots + uel_spots) return "uel";
  if (uecl_spots > 0 && position <= ucl_spots + uel_spots + uecl_spots) return "uecl";
  if (relegation_zone > 0 && position > actualTeamCount - relegation_zone) return "relegation";

  return null;
};

/** 3px coloured left edge per zone — reads like a printed league table */
const ZONE_EDGE: Record<Exclude<TableZone, null>, string> = {
  ucl: "border-l-zone-ucl",
  uel: "border-l-zone-uel",
  uecl: "border-l-zone-uecl",
  promotion: "border-l-zone-uecl",
  playoff: "border-l-zone-ucl",
  relegation: "border-l-zone-relegation",
};

const getZoneEdgeClass = (zone: TableZone) =>
  zone ? ZONE_EDGE[zone] : "border-l-transparent";


const LeagueTableView = ({ leagueCode, seasonType, onSelectManager, qualificationConfig, onTableLoaded }: LeagueTableViewProps) => {
  const [venueFilter, setVenueFilter] = useState<ViewMode>("all");
  const [showRawPoints, setShowRawPoints] = useState(false);
  const { seasonLabel, startDate, endDate, isOffSeason } = getSeasonDatesForLeague(seasonType);
  const { getClubSlug } = useClubSlugMap();

  // Fetch points deductions for this league/season
  const { data: deductionsData } = useQuery({
    queryKey: ["points-deductions", leagueCode, seasonLabel],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("points_deductions")
        .select("club_id, deduction, reason")
        .eq("league", leagueCode)
        .eq("season", seasonLabel);
      if (error) {
        console.error("[deductions] fetch error:", error);
        return [];
      }
      return data || [];
    },
    staleTime: 10 * 60 * 1000,
  });

  // Build deduction map: club_id -> total deduction
  const deductionMap = new Map<string, { total: number; reasons: string[] }>();
  (deductionsData || []).forEach((d) => {
    const existing = deductionMap.get(d.club_id) || { total: 0, reasons: [] };
    existing.total += d.deduction;
    if (d.reason) existing.reasons.push(d.reason);
    deductionMap.set(d.club_id, existing);
  });
  const hasDeductions = deductionMap.size > 0;

  const { data: tableData, isLoading } = useQuery({
    queryKey: ["current-league-table", leagueCode, seasonLabel, startDate, endDate],
    queryFn: async () => {
      // The whole table (points, home/away splits, current manager record) is
      // computed server-side in one round-trip instead of ~5 client queries.
      const { data, error } = await supabase.rpc("get_league_standings", {
        p_league: leagueCode,
        p_season_label: seasonLabel,
        p_start: startDate,
        p_end: endDate,
        p_team_count: qualificationConfig?.team_count || 20,
        p_season_type: seasonType,
      });

      if (error) throw error;

      return { table: ((data as unknown) as TableRow[]) || [], seasonLabel };
    },
    staleTime: 5 * 60 * 1000,
  });


  const table = tableData?.table || [];

  // Apply deductions to create effective points
  const tableWithDeductions = table.map((row) => {
    const ded = deductionMap.get(row.club_id);
    return {
      ...row,
      deduction: ded?.total || 0,
      deductionReasons: ded?.reasons || [],
      effectivePoints: row.points - (ded?.total || 0),
    };
  });

  // Sort by effective (deducted) points by default, raw points when toggled
  const deductionSortedTable = [...tableWithDeductions].sort((a, b) => {
    const aP = showRawPoints ? a.points : a.effectivePoints;
    const bP = showRawPoints ? b.points : b.effectivePoints;
    if (bP !== aP) return bP - aP;
    if (b.goal_difference !== a.goal_difference) return b.goal_difference - a.goal_difference;
    if (b.goals_for !== a.goals_for) return b.goals_for - a.goals_for;
    return a.club_name.localeCompare(b.club_name);
  });

  useEffect(() => {
    if (table.length > 0 && onTableLoaded) {
      onTableLoaded(table);
    }
  }, [table.length]);

  if (isLoading) {
    return <Skeleton className="h-[820px] w-full" />;
  }

  // Apply venue/view filter: re-sort by venue-specific or PPG stats
  const sortedTable = venueFilter === "all" 
    ? deductionSortedTable 
    : venueFilter === "ppg"
    ? [...deductionSortedTable].sort((a, b) => {
        if (b.mgr_ppg !== a.mgr_ppg) return b.mgr_ppg - a.mgr_ppg;
        if (b.mgr_played !== a.mgr_played) return b.mgr_played - a.mgr_played;
        return a.club_name.localeCompare(b.club_name);
      })
    : [...deductionSortedTable].sort((a, b) => {
        const ap = venueFilter === "home" ? a.home_points : a.away_points;
        const bp = venueFilter === "home" ? b.home_points : b.away_points;
        const agd = venueFilter === "home" ? a.home_gd : a.away_gd;
        const bgd = venueFilter === "home" ? b.home_gd : b.away_gd;
        const agf = venueFilter === "home" ? a.home_gf : a.away_gf;
        const bgf = venueFilter === "home" ? b.home_gf : b.away_gf;
        if (bp !== ap) return bp - ap;
        if (bgd !== agd) return bgd - agd;
        if (bgf !== agf) return bgf - agf;
        return a.club_name.localeCompare(b.club_name);
      });

  // Helper to get display values based on venue filter
  const getRowStats = (row: TableRow) => {
    if (venueFilter === "home") {
      return { p: row.home_played, w: row.home_won, d: row.home_drawn, l: row.home_lost, gf: row.home_gf, ga: row.home_ga, gd: row.home_gd, pts: row.home_points };
    }
    if (venueFilter === "away") {
      return { p: row.away_played, w: row.away_won, d: row.away_drawn, l: row.away_lost, gf: row.away_gf, ga: row.away_ga, gd: row.away_gd, pts: row.away_points };
    }
    return { p: row.played, w: row.won, d: row.drawn, l: row.lost, gf: row.goals_for, ga: row.goals_against, gd: row.goal_difference, pts: row.points };
  };

  if (table.length === 0) {
    return (
      <p className="text-muted-foreground text-center py-8">
        No match data available for {seasonLabel} season
      </p>
    );
  }

  // Check if this league has any European spots or promotion spots
  const hasEuropeanSpots = qualificationConfig && 
    (qualificationConfig.ucl_spots > 0 || qualificationConfig.uel_spots > 0 || qualificationConfig.uecl_spots > 0);
  const hasPromotionSpots = qualificationConfig && qualificationConfig.promotion_spots > 0;

  return (
    <div>
      {/* Venue filter toggle + raw points toggle */}
      <div className="flex items-center justify-between mb-2 gap-2">
        {hasDeductions && (
          <div className="flex items-center gap-2">
            <Switch
              id="raw-points"
              checked={showRawPoints}
              onCheckedChange={setShowRawPoints}
            />
            <Label htmlFor="raw-points" className="text-xs text-muted-foreground cursor-pointer">
              Raw points
            </Label>
          </div>
        )}
        {!hasDeductions && <div />}
        <ToggleGroup type="single" value={venueFilter} onValueChange={(v) => v && setVenueFilter(v as ViewMode)} size="sm">
          <ToggleGroupItem value="all" className="text-xs px-2.5">All</ToggleGroupItem>
          <ToggleGroupItem value="home" className="text-xs px-2.5 gap-1"><Home className="h-3 w-3" />Home</ToggleGroupItem>
          <ToggleGroupItem value="away" className="text-xs px-2.5 gap-1"><Plane className="h-3 w-3" />Away</ToggleGroupItem>
          <ToggleGroupItem value="ppg" className="text-xs px-2.5 gap-1"><TrendingUp className="h-3 w-3" />PPG</ToggleGroupItem>
        </ToggleGroup>
      </div>
      <ScrollX>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2 border-border">
            <th className="label-micro text-left py-2 pl-3 pr-2 text-muted-foreground w-8">#</th>
            {venueFilter === "ppg" ? (
              <>
                <th className="label-micro text-left py-2 px-2 text-muted-foreground">Manager</th>
                <th className="label-micro text-left py-2 px-2 text-muted-foreground hidden sm:table-cell">Club</th>
              </>
            ) : (
              <>
                <th className="label-micro text-left py-2 px-2 text-muted-foreground">Team</th>
                <th className="label-micro text-left py-2 px-2 text-muted-foreground hidden lg:table-cell">Manager</th>
              </>
            )}
            <th className="label-micro text-center py-2 px-2 text-muted-foreground">P</th>
            <th className="label-micro text-center py-2 px-2 text-muted-foreground hidden sm:table-cell">W</th>
            <th className="label-micro text-center py-2 px-2 text-muted-foreground hidden sm:table-cell">D</th>
            <th className="label-micro text-center py-2 px-2 text-muted-foreground hidden sm:table-cell">L</th>
            {venueFilter !== "ppg" && (
              <>
                <th className="label-micro text-center py-2 px-2 text-muted-foreground hidden md:table-cell">GF</th>
                <th className="label-micro text-center py-2 px-2 text-muted-foreground hidden md:table-cell">GA</th>
                <th className="label-micro text-center py-2 px-2 text-muted-foreground">GD</th>
              </>
            )}
            <th className="label-micro text-center py-2 px-2 text-muted-foreground">Pts</th>
            {venueFilter === "ppg" && (
              <th className="label-micro text-center py-2 px-2 text-muted-foreground">PPG</th>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedTable.map((row, index) => {
            const position = index + 1;
            const s = venueFilter === "ppg"
              ? { p: row.mgr_played, w: row.mgr_won, d: row.mgr_drawn, l: row.mgr_lost, gf: row.mgr_gf, ga: row.mgr_ga, gd: row.mgr_gd, pts: row.mgr_points }
              : getRowStats(row);
            const zone = venueFilter === "all"
              ? getPositionZone(position, sortedTable.length, qualificationConfig)
              : null;
            return (
              <tr
                key={row.club_id}
                className="border-b border-border/50 transition-colors hover:bg-muted/40"
              >
                <td
                  className={`num py-1.5 pl-3 pr-2 border-l-[3px] text-muted-foreground ${getZoneEdgeClass(zone)}`}
                >
                  {position}
                </td>

                {venueFilter === "ppg" ? (
                  <>
                    <td className="py-2 px-2 font-medium truncate max-w-[120px] sm:max-w-[160px]">
                      {row.manager_id && row.manager_name ? (
                        <span className="inline-flex items-center gap-1">
                          <ManagerLink
                            managerId={row.manager_id!}
                            onFallbackClick={() => onSelectManager?.(row.manager_id!)}
                            className="hover:text-primary hover:underline transition-colors text-left"
                          >
                            {row.manager_name}
                          </ManagerLink>
                          {row.is_mid_season_appointment && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <ArrowRightLeft className="h-3 w-3 text-amber-700 dark:text-amber-400 flex-shrink-0" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Mid-season appointment</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-muted-foreground hidden sm:table-cell">
                      <span className="flex items-center gap-2 min-w-0">
                        <ClubCrest primary={row.club_brand_primary} secondary={row.club_brand_secondary} clubName={row.club_name} size="sm" />
                        {getClubSlug(row.club_id) ? (
                          <Link
                            to={`/club/${getClubSlug(row.club_id)}`}
                            className="truncate max-w-[100px] hover:text-primary hover:underline transition-colors text-left"
                          >
                            {row.club_name}
                          </Link>
                        ) : (
                          <span className="truncate max-w-[100px]">{row.club_name}</span>
                        )}
                      </span>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="py-1.5 px-2 font-medium">
                      <span className="flex items-center gap-2 min-w-0">
                        <ClubCrest primary={row.club_brand_primary} secondary={row.club_brand_secondary} clubName={row.club_name} size="sm" />
                        {getClubSlug(row.club_id) ? (
                          <Link
                            to={`/club/${getClubSlug(row.club_id)}`}
                            className="truncate max-w-[100px] sm:max-w-[140px] hover:text-primary hover:underline transition-colors text-left"
                          >
                            {row.club_name}
                          </Link>
                        ) : (
                          <span className="truncate max-w-[100px] sm:max-w-[140px]">{row.club_name}</span>
                        )}
                      </span>
                    </td>

                    <td className="py-2 px-2 text-muted-foreground truncate max-w-[120px] hidden lg:table-cell">
                      {row.manager_id && row.manager_name ? (
                        <span className="inline-flex items-center gap-1">
                          <ManagerLink
                            managerId={row.manager_id!}
                            onFallbackClick={() => onSelectManager?.(row.manager_id!)}
                            className="hover:text-primary hover:underline transition-colors text-left"
                          >
                            {row.manager_name}
                          </ManagerLink>
                          {row.is_mid_season_appointment && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <ArrowRightLeft className="h-3 w-3 text-amber-700 dark:text-amber-400 flex-shrink-0" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Mid-season appointment</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                  </>
                )}
                <td className="num py-2 px-2 text-center">{s.p}</td>
                <td className="num py-2 px-2 text-center hidden sm:table-cell">{s.w}</td>
                <td className="num py-2 px-2 text-center hidden sm:table-cell">{s.d}</td>
                <td className="num py-2 px-2 text-center hidden sm:table-cell">{s.l}</td>
                {venueFilter !== "ppg" && (
                  <>
                    <td className="num py-2 px-2 text-center hidden md:table-cell">{s.gf}</td>
                    <td className="num py-2 px-2 text-center hidden md:table-cell">{s.ga}</td>
                    <td className={`num py-2 px-2 text-center ${
                      s.gd > 0 ? "text-signal" : s.gd < 0 ? "text-destructive" : ""
                    }`}>
                      {s.gd > 0 ? "+" : ""}{s.gd}
                    </td>
                  </>
                )}
                <td className="num py-2 px-2 text-center font-bold">
                  {venueFilter === "all" && !showRawPoints && row.deduction > 0 ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            {row.effectivePoints}
                            <span className="text-xs font-normal text-destructive ml-0.5">(-{row.deduction})</span>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{row.deductionReasons.length > 0 ? row.deductionReasons.join('; ') : `${row.deduction}pt deduction`}</p>
                          <p className="text-xs text-muted-foreground">Raw points: {s.pts}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : s.pts}
                </td>
                {venueFilter === "ppg" && (
                  <td className="num py-2 px-2 text-center font-bold text-primary">
                    {row.mgr_ppg.toFixed(2)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </ScrollX>
      
      {/* Qualification/Promotion Legend */}
      {venueFilter === "all" && (hasEuropeanSpots || hasPromotionSpots) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 label-micro text-muted-foreground">
          {qualificationConfig!.ucl_spots > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-0.5 h-3 rounded-full bg-zone-ucl" />
              Champions League
            </span>
          )}
          {qualificationConfig!.uel_spots > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-0.5 h-3 rounded-full bg-zone-uel" />
              Europa League
            </span>
          )}
          {qualificationConfig!.uecl_spots > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-0.5 h-3 rounded-full bg-zone-uecl" />
              Conference League
            </span>
          )}
          {hasPromotionSpots && !hasEuropeanSpots && (
            <>
              <span className="flex items-center gap-1.5">
                <span className="w-0.5 h-3 rounded-full bg-zone-uecl" />
                Promotion
              </span>
              {qualificationConfig!.playoff_spots > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="w-0.5 h-3 rounded-full bg-zone-ucl" />
                  Playoffs
                </span>
              )}
            </>
          )}
          {qualificationConfig!.relegation_zone > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-0.5 h-3 rounded-full bg-zone-relegation" />
              Relegation
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const CurrentLeagueTables = ({ onSelectManager, activeLeague: externalActiveLeague }: CurrentLeagueTablesProps) => {
  const { user } = useAuth();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [standingsTable, setStandingsTable] = useState<TableRow[]>([]);
  const activeLeague = externalActiveLeague ?? "ENG_PL";

  // Fetch active leagues from database to get season type and qualification config for active league
  const { data: dbLeagues, isLoading: isLoadingLeagues } = useQuery({
    queryKey: ['active-leagues-with-qualification'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leagues')
        .select('code, display_name, country, tier, season_type, ucl_spots, uel_spots, uecl_spots, relegation_zone, team_count, promotion_spots, playoff_spots')
        .eq('is_active', true)
        .order('display_name');
      
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Find season type and qualification config for active league
  const activeLeagueData = dbLeagues?.find(l => l.code === activeLeague);
  const activeSeasonType: SeasonType = (activeLeagueData?.season_type as SeasonType) || 'split-year';
  const { seasonLabel, isOffSeason } = getSeasonDatesForLeague(activeSeasonType);
  
  // Build qualification config for the active league
  const qualificationConfig: LeagueQualificationConfig | null = activeLeagueData ? {
    ucl_spots: activeLeagueData.ucl_spots ?? 0,
    uel_spots: activeLeagueData.uel_spots ?? 0,
    uecl_spots: activeLeagueData.uecl_spots ?? 0,
    relegation_zone: activeLeagueData.relegation_zone ?? 3,
    team_count: activeLeagueData.team_count ?? 20,
    promotion_spots: activeLeagueData.promotion_spots ?? 0,
    playoff_spots: activeLeagueData.playoff_spots ?? 0,
  } : null;

  const tableRef = useRef<HTMLDivElement>(null);

  // Fetch deductions for share dialog
  const { data: shareDeductions } = useQuery({
    queryKey: ["points-deductions-share", activeLeague, seasonLabel],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("points_deductions")
        .select("club_id, deduction")
        .eq("league", activeLeague)
        .eq("season", seasonLabel);
      if (error) return [];
      return data || [];
    },
    staleTime: 10 * 60 * 1000,
  });

  const shareDeductionMap = new Map<string, number>();
  (shareDeductions || []).forEach((d) => {
    shareDeductionMap.set(d.club_id, (shareDeductionMap.get(d.club_id) || 0) + d.deduction);
  });

  const handleTableLoaded = useCallback((table: TableRow[]) => {
    setStandingsTable(table);
  }, []);

  // Show loading if leagues are being fetched
  if (isLoadingLeagues) {
    return (
      <Card className="p-6">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-[820px] w-full" />
      </Card>
    );
  }

  // If no leagues synced yet
  if (!dbLeagues || dbLeagues.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Current Standings</h2>
        <p className="text-muted-foreground text-center py-8">
          No leagues synced yet. Go to Admin → API Data to sync leagues.
        </p>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-6" ref={tableRef}>
        <div className="flex items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-foreground">Current Standings</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {seasonLabel}
            </span>
            {IMAGE_SHARING_ENABLED && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                aria-label="Share league table"
                onClick={() => setShareDialogOpen(true)}
              >
                <Share2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Share</span>
              </Button>
            )}
          </div>
        </div>
        
        <LeagueTableView
          leagueCode={activeLeague}
          seasonType={activeSeasonType}
          onSelectManager={onSelectManager}
          qualificationConfig={qualificationConfig}
          onTableLoaded={handleTableLoaded}
        />
        
        {isOffSeason && (
          <p className="text-sm text-muted-foreground text-center mt-2">
            📅 This league is currently in off-season. Showing {seasonLabel} final standings.
          </p>
        )}
      </Card>

      {shareDialogOpen && IMAGE_SHARING_ENABLED && (
      <Suspense fallback={null}>
      <StandingsShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        leagueName={activeLeagueData?.display_name || activeLeague}
        season={seasonLabel}
        table={standingsTable.map(r => {
          const ded = shareDeductionMap.get(r.club_id) || 0;
          return {
            club_id: r.club_id,
            club_name: r.club_name,
            played: r.played,
            won: r.won,
            drawn: r.drawn,
            lost: r.lost,
            goals_for: r.goals_for,
            goals_against: r.goals_against,
            goal_difference: r.goal_difference,
            points: r.points,
            deduction: ded,
            effectivePoints: r.points - ded,
          };
        }).sort((a, b) => {
          if (b.effectivePoints !== a.effectivePoints) return b.effectivePoints - a.effectivePoints;
          if (b.goal_difference !== a.goal_difference) return b.goal_difference - a.goal_difference;
          if (b.goals_for !== a.goals_for) return b.goals_for - a.goals_for;
          return a.club_name.localeCompare(b.club_name);
        })}
        qualificationConfig={qualificationConfig}
      />
      </Suspense>
      )}

    </>
  );
};

export default CurrentLeagueTables;
